import { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { ArrowRight, Clock, CalendarBlank, Phone } from "@phosphor-icons/react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import { POSTS, BLOG_BASE } from "@/blogData";
import { SITE, PHONE_DISPLAY, PHONE_TEL } from "@/seoData";
import usePricing from "@/hooks/usePricing";
import { applyPrices } from "@/priceTokens";
import { sizedImg } from "@/lib/utils";

const Eyebrow = ({ children }) => (
  <div className="inline-flex items-center gap-2 text-[#46A01E] font-display font-bold uppercase tracking-widest text-xs mb-4">
    <span className="h-px w-8 bg-[#46A01E]" />
    {children}
  </div>
);

const fmtDate = (d) =>
  new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" });

export default function Blog() {
  const [rawPosts, setPosts] = useState(POSTS);
  const pricing = usePricing();
  const posts = applyPrices(rawPosts, pricing);

  useEffect(() => {
    window.scrollTo(0, 0);
    axios
      .get(`${process.env.REACT_APP_BACKEND_URL}/api/guides`)
      .then(({ data }) => Array.isArray(data) && data.length && setPosts(data))
      .catch(() => {});
  }, []);

  const canonical = `${SITE}/blog`;
  const title = "San Diego Junk Removal Guides & Tips | CleanOut247 Blog";
  const description =
    "Local guides on junk removal costs, mattress & appliance disposal, the Miramar Landfill and more — practical, San Diego–specific advice from CleanOut247.";

  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "CleanOut247 San Diego Junk Removal Guides",
      url: canonical,
      description,
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        url: `${BLOG_BASE}/${p.slug}`,
        datePublished: p.date,
        description: p.description,
      })),
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "Home", item: SITE + "/" },
        { "@type": "ListItem", position: 2, name: "Guides", item: canonical },
      ],
    },
  ];

  return (
    <div className="bg-white text-[#0A0A0A] overflow-x-hidden" data-testid="blog-page">
      <Seo title={title} description={description} canonical={canonical} jsonLd={jsonLd} />
      <Header />

      <section className="pt-28 sm:pt-40 lg:pt-48 pb-14 lg:pb-16 px-5 lg:px-8 bg-[#0B1E45] text-white">
        <div className="max-w-7xl mx-auto">
          <Eyebrow>Guides & Tips</Eyebrow>
          <h1 className="font-display font-black tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-4xl">
            San Diego junk removal guides.
          </h1>
          <p className="text-white/70 mt-6 text-lg leading-relaxed max-w-2xl">
            Straight answers to the questions San Diego homeowners actually ask —
            what things cost, how to dispose of tricky items, and how to do it the legal, hassle-free way.
          </p>
        </div>
      </section>

      <section className="px-5 lg:px-8 py-16 lg:py-24">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="blog-grid">
          {posts.map((p) => (
            <Link
              key={p.slug}
              to={`/blog/${p.slug}`}
              data-testid={`blog-card-${p.slug}`}
              className="group border border-[#E4E4E7] rounded-sm overflow-hidden flex flex-col hover:border-[#46A01E] hover:shadow-md transition-all"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={sizedImg(p.hero, 640)} alt={p.title} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="inline-block self-start bg-[#EAF4E3] text-[#15306E] text-xs font-semibold px-2.5 py-1 rounded-sm mb-3">{p.category}</span>
                <h2 className="font-display font-extrabold text-xl text-[#15306E] leading-snug group-hover:text-[#46A01E] transition-colors">{p.title}</h2>
                <p className="text-sm text-[#52525B] mt-2 leading-relaxed flex-1">{p.excerpt}</p>
                <div className="flex items-center gap-4 text-xs text-[#71717A] mt-4">
                  <span className="flex items-center gap-1"><CalendarBlank size={13} weight="fill" /> {fmtDate(p.date)}</span>
                  <span className="flex items-center gap-1"><Clock size={13} weight="fill" /> {p.readTime}</span>
                </div>
                <span className="flex items-center gap-1.5 text-[#46A01E] font-display font-bold text-sm mt-4">
                  Read guide <ArrowRight size={16} weight="bold" className="group-hover:translate-x-1 transition-transform" />
                </span>
              </div>
            </Link>
          ))}
        </div>
      </section>

      <section className="px-5 lg:px-8 pb-20 text-center">
        <div className="max-w-2xl mx-auto">
          <h2 className="font-display font-black tracking-tighter text-3xl lg:text-4xl text-[#15306E]">Ready to clear it out?</h2>
          <p className="text-[#52525B] mt-3">Same-day service across San Diego County — 7 days a week.</p>
          <a
            href={`tel:${PHONE_TEL}`}
            data-testid="blog-cta-call"
            className="inline-flex items-center gap-2 mt-6 bg-[#46A01E] hover:bg-[#3A8418] text-white font-display font-extrabold uppercase tracking-wide text-sm px-6 py-4 rounded-sm transition-colors"
          >
            <Phone size={18} weight="fill" /> Call/Text {PHONE_DISPLAY}
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
}
